
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Book, Plus, Search, Trash2, MapPin, Tag } from 'lucide-react';
import { useStore } from '../hooks/useStore.ts';
import { api } from '../services/api.ts';
import { KnowledgeBaseDocument } from '../types.ts';

export const KnowledgeBase: React.FC = () => {
    const { t } = useTranslation();
    const documents = useStore(() => api.knowledgeBase.list(), [], 'knowledgeBase');
    const [isAdding, setIsAdding] = useState(false);
    const [search, setSearch] = useState('');
    const [newItem, setNewItem] = useState<{ title: string; location: string; tags: string; content: string }>({
        title: '',
        location: '',
        tags: '',
        content: ''
    });

    const filteredDocs = documents?.filter(d =>
        d.title.toLowerCase().includes(search.toLowerCase()) ||
        d.content.toLowerCase().includes(search.toLowerCase()) ||
        d.location?.toLowerCase().includes(search.toLowerCase())
    ) || [];

    const handleAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newItem.title || !newItem.content) return;

        await api.knowledgeBase.add({
            title: newItem.title,
            content: newItem.content,
            location: newItem.location,
            tags: newItem.tags.split(',').map(s => s.trim()).filter(Boolean),
            createdAt: new Date().toISOString()
        });

        setIsAdding(false);
        setNewItem({ title: '', location: '', tags: '', content: '' });
    };

    const handleDelete = async (id: number) => {
        if (confirm(t('common.confirm_delete') || 'Are you sure?')) {
            await api.knowledgeBase.delete(id);
        }
    };

    return (
        <div className="space-y-8">
            <header className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-black tracking-tight">{t('nav.knowledge_base') || 'Knowledge Base'}</h1>
                    <p className="text-zinc-500 mt-2 font-medium">Manage regulatory documents and local rules for AI retrieval.</p>
                </div>
                <button
                    onClick={() => setIsAdding(!isAdding)}
                    className="flex items-center gap-2 px-5 py-2.5 bg-black text-white hover:bg-zinc-800 rounded-xl font-bold transition-all shadow-lg hover:shadow-xl active:scale-95"
                >
                    <Plus size={18} />
                    <span>Add Document</span>
                </button>
            </header>

            {isAdding && (
                <div className="bg-white p-6 rounded-2xl border border-zinc-200 shadow-sm animate-in fade-in slide-in-from-top-4">
                    <h2 className="text-lg font-bold mb-4">Add New Regulation</h2>
                    <form onSubmit={handleAdd} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold uppercase text-zinc-400 mb-1">Title</label>
                                <input
                                    className="w-full px-4 py-2 rounded-lg bg-zinc-50 border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-black/5"
                                    placeholder="e.g. Guam 2009 IBC Amendments"
                                    value={newItem.title}
                                    onChange={e => setNewItem({ ...newItem, title: e.target.value })}
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold uppercase text-zinc-400 mb-1">Location / Jurisdiction</label>
                                <input
                                    className="w-full px-4 py-2 rounded-lg bg-zinc-50 border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-black/5"
                                    placeholder="e.g. Guam, CNMI, Tumon"
                                    value={newItem.location}
                                    onChange={e => setNewItem({ ...newItem, location: e.target.value })}
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase text-zinc-400 mb-1">Tags (comma separated)</label>
                            <input
                                className="w-full px-4 py-2 rounded-lg bg-zinc-50 border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-black/5"
                                placeholder="zoning, safety, electrical"
                                value={newItem.tags}
                                onChange={e => setNewItem({ ...newItem, tags: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase text-zinc-400 mb-1">Content / Rule Text</label>
                            <textarea
                                className="w-full h-48 px-4 py-3 rounded-lg bg-zinc-50 border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-black/5 font-mono text-sm"
                                placeholder="Paste the full text of the regulation here..."
                                value={newItem.content}
                                onChange={e => setNewItem({ ...newItem, content: e.target.value })}
                                required
                            />
                        </div>
                        <div className="flex justify-end gap-3 pt-2">
                            <button
                                type="button"
                                onClick={() => setIsAdding(false)}
                                className="px-4 py-2 text-zinc-500 font-bold hover:text-black hover:bg-zinc-100 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-6 py-2 bg-black text-white font-bold rounded-lg hover:bg-zinc-800 transition-shadow shadow-md"
                            >
                                Save Document
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                <input
                    type="text"
                    placeholder="Search knowledge base..."
                    className="w-full pl-12 pr-4 py-3 bg-white rounded-xl border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-black/5 transition-all shadow-sm"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            <div className="grid grid-cols-1 gap-4">
                {filteredDocs.map((doc) => (
                    <div key={doc.id} className="group bg-white p-5 rounded-xl border border-zinc-100 hover:border-zinc-300 transition-all shadow-sm hover:shadow-md">
                        <div className="flex justify-between items-start">
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                                    <Book size={20} />
                                </div>
                                <div>
                                    <h3 className="font-bold text-lg text-zinc-900 group-hover:text-emerald-700 transition-colors">{doc.title}</h3>
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {doc.location && (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-zinc-50 text-zinc-500 text-xs font-bold uppercase tracking-wide">
                                                <MapPin size={10} />
                                                {doc.location}
                                            </span>
                                        )}
                                        {doc.tags?.map((tag, i) => (
                                            <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-zinc-50 text-zinc-500 text-xs font-bold uppercase tracking-wide">
                                                <Tag size={10} />
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                    <p className="mt-3 text-zinc-600 text-sm line-clamp-3 leading-relaxed border-l-2 border-zinc-100 pl-3">
                                        {doc.content}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => handleDelete(doc.id!)}
                                className="text-zinc-300 hover:text-red-500 hover:bg-red-50 p-2 rounded-lg transition-colors"
                            >
                                <Trash2 size={18} />
                            </button>
                        </div>
                    </div>
                ))}

                {filteredDocs.length === 0 && (
                    <div className="text-center py-12 bg-zinc-50 rounded-2xl border border-dashed border-zinc-200">
                        <Book className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
                        <h3 className="text-zinc-900 font-bold">No documents found</h3>
                        <p className="text-zinc-500 text-sm mt-1">Add regulations to make your AI smarter.</p>
                    </div>
                )}
            </div>
        </div>
    );
};
